"use client"

import React from "react"
import Link from "next/link"
import { AlertTriangle, RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"

interface ErrorPageProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function SoundDetailError({ error, reset }: ErrorPageProps) {
  const retryRef = React.useRef<HTMLButtonElement>(null)

  // §07.8: the page boundary hands focus to retry so keyboard users are not
  // stranded mid-page.
  React.useEffect(() => {
    retryRef.current?.focus()
  }, [])

  React.useEffect(() => {
    import('@sentry/browser').then(({ captureException }) => {
      captureException(error, { tags: { context: 'SoundDetailErrorBoundary' } })
    }).catch(() => {})
  }, [error])

  return (
    <div className="relative min-h-svh overflow-hidden bg-[var(--tls-canvas)]">
      <div className="relative mx-auto flex min-h-svh w-full max-w-5xl items-center justify-center p-4 sm:p-6">
        {/* A route boundary, not one of PRODUCT.md's bounded failure surfaces
            (global error, 404, audio diagnostic), so it gets a hairline and
            no contained ground. */}
        <div className="w-full max-w-lg border-t border-[color:var(--tls-border-subtle)] pt-8 text-center">
          <AlertTriangle aria-hidden="true" className="mx-auto mb-5 size-8 text-[var(--tls-fg-strong)]" />

          <div className="space-y-2">
            <h1 className="font-display text-2xl text-[var(--tls-fg-strong)]">
              This sound did not load
            </h1>
            <p className="text-sm text-[var(--tls-fg-muted)] sm:text-base">
              Press Try again to reload the page, or pick another sound from
              the library. Anything you already downloaded still works.
            </p>
          </div>

          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
            <Button ref={retryRef} onClick={reset} className="font-display">
              <RefreshCw className="size-4" />
              Try again
            </Button>
            <Button asChild variant="outline" className="font-display">
              <Link href="/sounds">
                Browse sounds
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
